import { Button } from '../Button'
import { useDialogKeyboardShortcuts } from '../../hooks/useDialogKeyboardShortcuts'
import { openExternalBillingUrl } from '../../lib/authSessionPersistence'
import { supabase } from '../../lib/supabase'
import { createBillingCheckout, type BillingPlanCheckout } from '../../lib/billing/api'

export interface QuotaExhaustedModalProps {
  isOpen: boolean
  tier: 'free' | 'pro'
  used: number
  cap: number
  reason?: string | null
  duplicateLayer?: string | null
  onClose: () => void
}

export default function QuotaExhaustedModal({
  isOpen,
  tier,
  used,
  cap,
  reason,
  duplicateLayer,
  onClose,
}: QuotaExhaustedModalProps) {
  useDialogKeyboardShortcuts({ isOpen, onCancel: onClose })

  if (!isOpen) return null

  const isPro = tier === 'pro'
  const isDuplicateRisk = reason === 'duplicate_risk'
  const isDeviceDuplicate = isDuplicateRisk && duplicateLayer === 'device'
  const isEmailDuplicate = isDuplicateRisk && duplicateLayer === 'email'
  const title = isDeviceDuplicate
    ? 'Free plan already used on this device'
    : isEmailDuplicate
      ? 'Free plan already used with this email'
      : isDuplicateRisk
        ? 'Resume already used on a free account'
        : isPro
          ? 'Plan limit reached'
          : 'No optimizations left'
  const body = isDeviceDuplicate
    ? 'This device already used the free plan on another account. Upgrade to continue optimizing.'
    : isEmailDuplicate
      ? 'Another account with the same email address already used the free lifetime limit. Upgrade to Pro or contact support if this is a mistake.'
      : isDuplicateRisk
        ? 'This resume was already used on another free account that reached the lifetime limit. Upgrade to Pro or contact support if this is a mistake.'
        : isPro
        ? `You used all ${cap} optimizations in this billing period. Buy a +5 pack to continue now, or wait until your plan renews.`
        : `You used all ${cap} free optimizations (10 lifetime cap). Try Pro for $7/week, upgrade to Pro for 100/month, or buy a +5 pack for this job.`

  const startCheckout = async (plan: BillingPlanCheckout) => {
    try {
      const url = await createBillingCheckout(plan)
      await openExternalBillingUrl(url, supabase)
    } catch {
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto" data-testid="quota-exhausted-modal">
      <div
        className="fixed inset-0 bg-overlay"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />
      <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="quota-modal-title"
          className="relative w-full max-w-md rounded-lg bg-surface-default shadow-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-5 space-y-4">
            <div
              className="inline-flex items-center gap-2 rounded-full bg-danger-subtle px-3 py-1 text-small text-danger-strong"
              data-testid="quota-modal-meter"
            >
              {used} of {cap} used
            </div>
            <h2 id="quota-modal-title" className="text-h3 font-semibold text-text-primary">
              {title}
            </h2>
            <p className="text-body text-text-secondary">{body}</p>
            <p className="text-small text-text-muted">
              You can still edit, preview, and export your resume.
            </p>
          </div>
          <div className="flex flex-col gap-2 px-6 py-4 bg-surface-subtle rounded-b-lg">
            <Button
              type="button"
              variant="primary"
              primaryWeight="medium"
              className="w-full min-h-[44px]"
              onClick={() => void startCheckout('overage_pack')}
            >
              Get 5 more — $5
            </Button>
            {!isPro && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  primaryWeight="medium"
                  className="w-full min-h-[44px]"
                  onClick={() => void startCheckout('pro_try_weekly')}
                >
                  Pro Try — $7/week
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  primaryWeight="medium"
                  className="w-full min-h-[44px]"
                  onClick={() => void startCheckout('pro_monthly')}
                >
                  Pro — $20/month
                </Button>
              </>
            )}
            <Button
              type="button"
              variant="ghost"
              primaryWeight="medium"
              className="w-full min-h-[40px]"
              onClick={onClose}
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
